// src/pages/finance-portal/ar-aging/ARAgingReport.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Card, TextField, Autocomplete,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Skeleton, Alert, CircularProgress, Collapse, IconButton, Tooltip,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function todayISO() {
    const d = new Date();
    return d.toISOString().split('T')[0];
}

const BUCKETS = [
    { key: 'current', label: 'Current', color: '#22c55e', bg: '#dcfce7' },
    { key: 'days_1_30', label: '1-30 Days', color: '#eab308', bg: '#fef9c3' },
    { key: 'days_31_60', label: '31-60 Days', color: '#f97316', bg: '#ffedd5' },
    { key: 'days_61_90', label: '61-90 Days', color: '#ea580c', bg: '#fed7aa' },
    { key: 'days_90_plus', label: '90+ Days', color: '#dc2626', bg: '#fecaca' },
];

function getPriorityColor(score) {
    if (score > 10000) return { bg: '#fecaca', color: '#991b1b', label: 'High' };
    if (score >= 1000) return { bg: '#ffedd5', color: '#9a3412', label: 'Medium' };
    return { bg: '#dcfce7', color: '#166534', label: 'Low' };
}

function CustomerRow({ row }) {
    const [open, setOpen] = useState(false);
    const priority = getPriorityColor(row.priority_score || 0);

    return (
        <>
            <TableRow
                sx={{
                    '&:hover': { bgcolor: '#f8fafc' },
                    cursor: row.invoices && row.invoices.length > 0 ? 'pointer' : 'default',
                }}
                onClick={() => {
                    if (row.invoices && row.invoices.length > 0) setOpen(!open);
                }}
            >
                <TableCell sx={{ fontFamily, width: 40, p: 1 }}>
                    {row.invoices && row.invoices.length > 0 && (
                        <IconButton size="small">
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600 }}>{row.customer_name}</TableCell>
                {BUCKETS.map((b) => (
                    <TableCell key={b.key} align="right" sx={{ fontFamily }}>
                        {formatCurrency(row[b.key])}
                    </TableCell>
                ))}
                <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>
                    {formatCurrency(row.total)}
                </TableCell>
                <TableCell align="center" sx={{ fontFamily }}>{row.invoice_count || 0}</TableCell>
                <TableCell align="center" sx={{ fontFamily }}>
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            bgcolor: priority.bg,
                            color: priority.color,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                        }}
                    >
                        {Math.round(row.priority_score || 0).toLocaleString()} ({priority.label})
                    </Box>
                </TableCell>
            </TableRow>
            {row.invoices && row.invoices.length > 0 && (
                <TableRow>
                    <TableCell colSpan={10} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 4 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontFamily, fontWeight: 700, mb: 1, color: '#475569' }}
                                >
                                    Invoices for {row.customer_name}
                                </Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Invoice #', 'Date', 'Due Date', 'Amount', 'Balance', 'Days Overdue', 'Bucket'].map((h) => (
                                                <TableCell
                                                    key={h}
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 700,
                                                        fontSize: '0.75rem',
                                                        color: '#64748b',
                                                        textTransform: 'uppercase',
                                                    }}
                                                >
                                                    {h}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {row.invoices.map((inv, idx) => {
                                            const bucket = BUCKETS.find((b) => b.key === inv.bucket) || BUCKETS[0];
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>
                                                        {inv.invoice_number || `INV-${inv.id}`}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily }}>{formatDate(inv.invoice_date)}</TableCell>
                                                    <TableCell sx={{ fontFamily }}>{formatDate(inv.due_date)}</TableCell>
                                                    <TableCell sx={{ fontFamily }}>{formatCurrency(inv.total_amount)}</TableCell>
                                                    <TableCell sx={{ fontFamily, fontWeight: 600 }}>
                                                        {formatCurrency(inv.balance_due)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily }}>{inv.days_overdue || 0}</TableCell>
                                                    <TableCell sx={{ fontFamily }}>
                                                        <Box
                                                            sx={{
                                                                display: 'inline-block',
                                                                px: 1,
                                                                py: 0.25,
                                                                borderRadius: '6px',
                                                                bgcolor: bucket.bg,
                                                                color: bucket.color,
                                                                fontWeight: 600,
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            {bucket.label}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

export default function ARAgingReport() {
    const [asOfDate, setAsOfDate] = useState(todayISO());
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerLoading, setCustomerLoading] = useState(false);
    const customerTimer = useRef(null);

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchCustomers = useCallback((search) => {
        if (customerTimer.current) clearTimeout(customerTimer.current);
        customerTimer.current = setTimeout(async () => {
            setCustomerLoading(true);
            try {
                const res = await axiosInstance.get('/api/finance/customers', {
                    params: { search: search || '' },
                });
                const list = res.data?.data || res.data?.customers || res.data || [];
                setCustomerOptions(Array.isArray(list) ? list : []);
            } catch {
                setCustomerOptions([]);
            } finally {
                setCustomerLoading(false);
            }
        }, 300);
    }, []);

    useEffect(() => {
        fetchCustomers('');
    }, [fetchCustomers]);

    const fetchReport = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { as_of_date: asOfDate };
            if (selectedCustomer) params.customer_id = selectedCustomer.id;
            const res = await axiosInstance.get('/api/finance/ar-aging', { params });
            setData(res.data?.data || res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load AR Aging Report.');
        } finally {
            setIsLoading(false);
        }
    }, [asOfDate, selectedCustomer]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const summary = data?.summary || {};
    const customers = data?.customers || [];

    const sortedCustomers = [...customers].sort(
        (a, b) => (b.priority_score || 0) - (a.priority_score || 0)
    );

    const grandTotal =
        (summary.current || 0) +
        (summary.days_1_30 || 0) +
        (summary.days_31_60 || 0) +
        (summary.days_61_90 || 0) +
        (summary.days_90_plus || 0);

    return (
        <Box sx={{ p: 3, fontFamily }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <AssessmentIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 700 }}>
                            AR Aging Report
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.9rem', opacity: 0.9 }}>
                            Track outstanding receivables by aging bucket
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Filters */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <Autocomplete
                    sx={{ minWidth: 280, flex: 1 }}
                    options={customerOptions}
                    getOptionLabel={(opt) => opt.name || opt.company_name || ''}
                    value={selectedCustomer}
                    onChange={(_, val) => setSelectedCustomer(val)}
                    onInputChange={(_, val) => {
                        setCustomerSearch(val);
                        fetchCustomers(val);
                    }}
                    loading={customerLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Customer (optional)"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
                                '& .MuiInputLabel-root': { fontFamily },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {customerLoading ? <CircularProgress size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <TextField
                    label="As of Date"
                    type="date"
                    size="small"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        minWidth: 180,
                        '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
                        '& .MuiInputLabel-root': { fontFamily },
                    }}
                />
            </Paper>

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }}>
                    {error}
                </Alert>
            )}

            {/* Summary Cards */}
            {isLoading ? (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    {[...Array(6)].map((_, i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            sx={{ flex: '1 1 160px', height: 100, borderRadius: '16px' }}
                        />
                    ))}
                </Box>
            ) : (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    {BUCKETS.map((b) => (
                        <Card
                            key={b.key}
                            elevation={0}
                            sx={{
                                flex: '1 1 160px',
                                p: 2.5,
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                background: `linear-gradient(135deg, ${b.bg} 0%, #fff 100%)`,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: b.color,
                                    textTransform: 'uppercase',
                                    mb: 0.5,
                                }}
                            >
                                {b.label}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' }}>
                                {formatCurrency(summary[b.key])}
                            </Typography>
                        </Card>
                    ))}
                    <Card
                        elevation={0}
                        sx={{
                            flex: '1 1 160px',
                            p: 2.5,
                            borderRadius: '16px',
                            border: '2px solid #22c55e',
                            background: 'linear-gradient(135deg, #f0fdf4 0%, #fff 100%)',
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#16a34a',
                                textTransform: 'uppercase',
                                mb: 0.5,
                            }}
                        >
                            Grand Total
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' }}>
                            {formatCurrency(grandTotal)}
                        </Typography>
                    </Card>
                </Box>
            )}

            {/* Customer Breakdown Table */}
            <Paper
                elevation={0}
                sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}
            >
                <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                        Customer Breakdown
                    </Typography>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', width: 40 }} />
                                {['Customer', 'Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days', 'Total', '# Invoices', 'Priority Score'].map(
                                    (h) => (
                                        <TableCell
                                            key={h}
                                            align={
                                                ['Customer'].includes(h)
                                                    ? 'left'
                                                    : ['# Invoices', 'Priority Score'].includes(h)
                                                    ? 'center'
                                                    : 'right'
                                            }
                                            sx={{
                                                fontFamily,
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                color: '#64748b',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {h}
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        {[...Array(10)].map((__, j) => (
                                            <TableCell key={j}>
                                                <Skeleton variant="text" sx={{ fontFamily }} />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : sortedCustomers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>
                                            No outstanding receivables found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedCustomers.map((row, idx) => (
                                    <CustomerRow key={row.customer_id || idx} row={row} />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}
